import { useEffect, useState } from "react";
import { useConfigStore } from "@/store/useConfigStore";

export default function ThemeProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const { config } = useConfigStore();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    const root = document.documentElement;

    // Primary colors
    root.style.setProperty("--primary", config.primaryColor || "#128f8b");
    root.style.setProperty(
      "--primary-hover",
      config.primaryHoverColor || "#0f7a76"
    );

    // Secondary colors
    root.style.setProperty("--secondary", config.secondaryColor || "#878a99");

    // Secondary hover (slightly darker)
    const secondaryColor = config.secondaryColor || "#878a99";
    const darkerSecondary = adjustColorBrightness(secondaryColor, -10);
    root.style.setProperty("--secondary-hover", darkerSecondary);

    // Destructive colors
    root.style.setProperty("--destructive", "#ef4444");
    root.style.setProperty("--destructive-hover", "#dc2626");

    // Foreground colors
    root.style.setProperty("--primary-foreground", "#ffffff");
    root.style.setProperty("--secondary-foreground", "#ffffff");
    root.style.setProperty("--destructive-foreground", "#ffffff");

    // Muted color for text
    root.style.setProperty("--muted", "#64748b");
  }, [
    config.primaryColor,
    config.primaryHoverColor,
    config.secondaryColor,
    mounted
  ]);

  // Prevent flash of unstyled content during SSR
  if (!mounted) {
    return null;
  }

  return <>{children}</>;
}

// Helper function to darken/lighten a hex color
function adjustColorBrightness(hex: string, percent: number): string {
  // Remove the hash if it exists
  hex = hex.replace(/^#/, "");

  // Parse the hex values
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  // Adjust each color component
  const adjustColor = (color: number) => {
    return Math.min(255, Math.max(0, Math.round(color * (1 + percent / 100))));
  };

  const newR = adjustColor(r);
  const newG = adjustColor(g);
  const newB = adjustColor(b);

  // Convert back to hex
  const toHex = (n: number) => {
    const hex = n.toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };

  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
}
